(() => {
  const ROOT_SELECTOR = '[data-ag-estimated-delivery-date]';
  const PRODUCT_GROUP_JSON_LD_SCRIPT_ID = 'ag-product-group-structured-data';
  const BUSINESS_DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

  const normalizeString = (value) => String(value ?? '').trim();
  const toUpperCase = (value) => normalizeString(value).toUpperCase();
  const toNumber = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  };

  const parseIsoDateAsStableUtc = (isoDate) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(normalizeString(isoDate));
    if (!match) return null;

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    return new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  };

  const buildFormattingLocale = (root) => {
    const explicitLocale = normalizeString(root.dataset.localeCode);
    if (explicitLocale.includes('-')) return explicitLocale;

    const languageCode = normalizeString(root.dataset.languageCode);
    const countryIsoCode = normalizeString(root.dataset.countryIsoCode).toUpperCase();

    if (languageCode && countryIsoCode) {
      return `${languageCode}-${countryIsoCode}`;
    }

    return explicitLocale || languageCode || document.documentElement.lang || 'en';
  };

  const buildProxyUrl = (root) => {
    const proxyPath = normalizeString(root.dataset.proxyPath);
    const countryIsoCode = normalizeString(root.dataset.countryIsoCode).toUpperCase();
    const locale = buildFormattingLocale(root);
    const currencyCode = toUpperCase(root.dataset.currencyCode);
    const shippingPolicyUrl = normalizeString(root.dataset.shippingPolicyUrl);

    if (!proxyPath || !countryIsoCode) return null;

    const url = new URL(proxyPath, window.location.origin);
    url.searchParams.set('country_iso_code', countryIsoCode);
    if (currencyCode) {
      url.searchParams.set('currency', currencyCode);
    }
    if (locale) {
      url.searchParams.set('locale', locale);
    }
    if (shippingPolicyUrl) {
      url.searchParams.set('policy_url', shippingPolicyUrl);
    }

    return url;
  };

  const buildBusinessDays = () =>
    BUSINESS_DAY_NAMES.map((dayOfWeek) => ({
      '@type': 'OpeningHoursSpecification',
      dayOfWeek,
    }));

  const buildDeliveryTime = (handlingBusinessDays, transitBusinessDays) => ({
    '@type': 'ShippingDeliveryTime',
    handlingTime: {
      '@type': 'QuantitativeValue',
      minValue: handlingBusinessDays,
      maxValue: handlingBusinessDays,
      unitCode: 'd',
    },
    transitTime: {
      '@type': 'QuantitativeValue',
      minValue: transitBusinessDays,
      maxValue: transitBusinessDays,
      unitCode: 'd',
    },
    businessDays: buildBusinessDays(),
  });

  const buildOfferShippingDetails = (group) => {
    const destinationCountries = Array.isArray(group && group.country_iso_codes)
      ? group.country_iso_codes.map((country) => toUpperCase(country)).filter(Boolean)
      : [];
    const currency = toUpperCase(group && group.currency);
    const handlingBusinessDays = toNumber(group && group.handling_business_days);
    const transitBusinessDays = toNumber(group && group.transit_business_days);

    if (
      destinationCountries.length === 0 ||
      !currency ||
      handlingBusinessDays === null ||
      transitBusinessDays === null
    ) {
      return null;
    }

    return {
      '@type': 'OfferShippingDetails',
      shippingRate: {
        '@type': 'MonetaryAmount',
        value: '0',
        currency,
      },
      shippingDestination: destinationCountries.map((country) => ({
        '@type': 'DefinedRegion',
        addressCountry: country,
      })),
      deliveryTime: buildDeliveryTime(handlingBusinessDays, transitBusinessDays),
    };
  };

  const mergeShippingIntoProductGroup = (payload) => {
    const shippingPolicyGroups = Array.isArray(payload && payload.shipping_policy_groups)
      ? payload.shipping_policy_groups
      : [];
    const offerShippingDetails = shippingPolicyGroups
      .map((group) => buildOfferShippingDetails(group))
      .filter(Boolean);
    const script = document.getElementById(PRODUCT_GROUP_JSON_LD_SCRIPT_ID);

    if (!(script instanceof HTMLScriptElement) || offerShippingDetails.length === 0) {
      return;
    }

    try {
      const parsed = JSON.parse(script.textContent || '{}');
      const variants = Array.isArray(parsed && parsed.hasVariant) ? parsed.hasVariant : [];

      if (parsed.offers && typeof parsed.offers === 'object') {
        parsed.offers = {
          ...parsed.offers,
          shippingDetails: offerShippingDetails,
        };
      }

      parsed.hasVariant = variants.map((variant) => {
        if (!variant || typeof variant !== 'object') return variant;
        const offers = variant.offers;
        if (!offers || typeof offers !== 'object') return variant;

        return {
          ...variant,
          offers: {
            ...offers,
            shippingDetails: offerShippingDetails,
          },
        };
      });

      script.textContent = JSON.stringify(parsed);
    } catch (_error) {
      console.warn('[AG Estimated Delivery Date] failed to merge shipping into ProductGroup schema');
    }
  };

  const formatDate = (isoDate, locale) => {
    const date = parseIsoDateAsStableUtc(isoDate);
    if (!date) return '';

    try {
      return new Intl.DateTimeFormat(locale || undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      }).format(date);
    } catch (_error) {
      return isoDate;
    }
  };

  const getTextNode = (root) => root.querySelector('[data-ag-estimated-delivery-date-text]');

  const setState = (root, state, text) => {
    root.dataset.state = state;
    const textNode = getTextNode(root);
    if (!(textNode instanceof HTMLElement)) return;
    textNode.textContent = text;
  };

  const renderLabel = (root, prefix, formattedDate) => {
    const textNode = getTextNode(root);
    if (!(textNode instanceof HTMLElement)) return;

    textNode.textContent = '';

    if (prefix) {
      textNode.append(document.createTextNode(`${prefix} `));
    }

    const dateNode = document.createElement('strong');
    dateNode.className = 'ag-estimated-delivery-date__date';
    dateNode.textContent = formattedDate;
    textNode.append(dateNode);
  };

  const renderSuccess = (root, isoDate) => {
    const locale = buildFormattingLocale(root);
    const formattedDate = formatDate(isoDate, locale);
    if (!formattedDate) {
      setState(root, 'fallback', normalizeString(root.dataset.fallbackMessage));
      return;
    }

    root.dataset.state = 'ready';
    renderLabel(root, normalizeString(root.dataset.labelPrefix), formattedDate);
  };

  const initRoot = async (root) => {
    if (!(root instanceof HTMLElement)) return;
    if (root.dataset.agEstimatedDeliveryDateInit === 'true') return;
    root.dataset.agEstimatedDeliveryDateInit = 'true';

    const proxyUrl = buildProxyUrl(root);
    const fallbackMessage = normalizeString(root.dataset.fallbackMessage);

    if (!proxyUrl) {
      setState(root, 'fallback', fallbackMessage);
      return;
    }

    setState(root, 'loading', fallbackMessage);

    try {
      const response = await fetch(proxyUrl.toString(), {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Unexpected status ${response.status}`);
      }

      const payload = await response.json();
      const isoDate = normalizeString(payload && payload.estimated_delivery_date);
      if (!isoDate) {
        throw new Error('Missing estimated_delivery_date');
      }

      renderSuccess(root, isoDate);
      mergeShippingIntoProductGroup(payload);
    } catch (error) {
      console.warn('[AG Estimated Delivery Date] failed to resolve delivery date', error);
      setState(root, 'fallback', fallbackMessage);
    }
  };

  const initAll = () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      initRoot(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.matches(ROOT_SELECTOR)) {
      initRoot(event.target);
      return;
    }
    event.target.querySelectorAll(ROOT_SELECTOR).forEach((root) => initRoot(root));
  });
})();
